import React from 'react';
import { BLUE_LIGHT, GREEN, BLUE_DARK } from '../../../constants/chartColors';

interface SelectedAccountData {
  account_name: string;
  aws_charges: number;
  credits_consumed: number;
  net_spend: number;
}

interface BurnRateDataItem {
  date: string;
  planned?: number | null;
  actual?: number | null;
  gross_cost?: number | null;
  credits_applied?: number | null;
  credits_remaining?: number | null;
  selected_account?: SelectedAccountData | null;
}

interface BurnRateChartProps {
  data: BurnRateDataItem[] | null;
  loading: boolean;
}

const BurnRateChart: React.FC<BurnRateChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Burn Rate: Planned vs Actual
        </h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Burn Rate: Planned vs Actual
        </h3>
        <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          <svg className="h-16 w-16 mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-center">
            No cost data available yet
            <br />
            <span className="text-sm">Cost data will appear once AWS environments are synced</span>
          </p>
        </div>
      </div>
    );
  }

  // Check if we have credits data (any data point has credits info)
  const hasCreditsData = data.some(d => d.gross_cost != null);

  // Check if we have account breakdown data
  const hasAccountBreakdown = data.some(d => d.selected_account != null);

  // Find max value for scaling
  const maxValue = Math.max(
    ...data.map((d) => {
      if (hasCreditsData) {
        // When showing credits, include credits_remaining in max calculation
        const orgValues = [d.planned || 0, d.gross_cost || 0, d.credits_remaining || 0, Math.abs((d.gross_cost || 0) - (d.credits_applied || 0))];

        // Include account values if present
        if (d.selected_account) {
          orgValues.push(d.selected_account.aws_charges || 0);
          orgValues.push(d.selected_account.credits_consumed || 0);
          orgValues.push(d.selected_account.net_spend || 0);
        }

        return Math.max(...orgValues);
      }
      return Math.max(d.planned || 0, d.actual || 0);
    })
  );

  // Dynamically determine label frequency and format based on data length
  const getLabelConfig = (): { interval: number; format: string } => {
    const dataLength = data.length;
    if (dataLength <= 31) {
      return { interval: 5, format: 'M/d' };        // ~1 month: every 5 days, show "10/15"
    } else if (dataLength <= 90) {
      return { interval: 7, format: 'M/d' };        // ~3 months: every week, show "10/15"
    } else if (dataLength <= 180) {
      return { interval: 14, format: 'M/d' };       // ~6 months: every 2 weeks, show "10/15"
    } else {
      return { interval: 30, format: 'M/d' };       // ~12 months: every month, show "10/1"
    }
  };

  const labelConfig = getLabelConfig();
  const showLabel = (index: number): boolean => {
    if (index === 0) return false; // Skip first label to avoid y-axis overlap

    const isIntervalLabel = index % labelConfig.interval === 0;
    const isLastLabel = index === data.length - 1;

    // Don't show last label if it would be a duplicate or too close to an interval label
    if (isLastLabel) {
      const lastIntervalIndex = Math.floor((data.length - 1) / labelConfig.interval) * labelConfig.interval;
      const distanceFromLastInterval = (data.length - 1) - lastIntervalIndex;

      // Skip if same date as interval label or if very close (within 3 days or less than half interval)
      if (distanceFromLastInterval === 0 || distanceFromLastInterval <= 3 || distanceFromLastInterval < labelConfig.interval * 0.5) {
        return false;
      }
      return true;
    }

    return isIntervalLabel;
  };

  const formatDateLabel = (dateStr: string): string => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {hasCreditsData ? 'Cost Trend with AWS Credits' : 'Burn Rate: Planned vs Actual'}
        </h3>
        <div className="flex items-center flex-wrap gap-x-4 gap-y-2 text-sm">
          {hasCreditsData ? (
            <>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-400 rounded-full mr-2"></div>
                <span className="text-gray-600 dark:text-gray-400">Org AWS Charges</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-gray-600 dark:text-gray-400">All Available Credits</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>
                <span className="text-gray-600 dark:text-gray-400">Org Actual Spend</span>
              </div>
              {hasAccountBreakdown && (
                <>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-400 rounded-full mr-2"></div>
                    <span className="text-gray-600 dark:text-gray-400">Account AWS Charges</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                    <span className="text-gray-600 dark:text-gray-400">Account Credits Used</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-600 rounded-full mr-2"></div>
                    <span className="text-gray-600 dark:text-gray-400">Account Actual Spend</span>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-400 rounded-full mr-2"></div>
                <span className="text-gray-600 dark:text-gray-400">Planned</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-600 rounded-full mr-2"></div>
                <span className="text-gray-600 dark:text-gray-400">Actual</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Simple line chart visualization */}
      <div className="relative h-64">
        <svg className="w-full h-full" viewBox="0 0 800 256">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((percent) => (
            <line
              key={percent}
              x1="40"
              y1={256 - percent * 230 + 10}
              x2="790"
              y2={256 - percent * 230 + 10}
              stroke="currentColor"
              strokeWidth="1"
              className="text-gray-200 dark:text-gray-700"
            />
          ))}

          {hasCreditsData ? (
            <>
              {/* AWS Charges line (light blue) */}
              <polyline
                fill="none"
                stroke={BLUE_LIGHT}
                strokeWidth="3"
                points={data
                  .map((d, i) => {
                    const x = 40 + (i / (data.length - 1)) * 750;
                    const y = 256 - ((d.gross_cost || 0) / maxValue) * 230 - 10;
                    return `${x},${y}`;
                  })
                  .join(' ')}
              />

              {/* Available Credits line (green-500) */}
              <polyline
                fill="none"
                stroke={GREEN}
                strokeWidth="2"
                points={data
                  .map((d, i) => {
                    const x = 40 + (i / (data.length - 1)) * 750;
                    // Available credits = total credits allocated - credits used so far
                    const availableCredits = (d.credits_remaining || 0);
                    const y = 256 - (availableCredits / maxValue) * 230 - 10;
                    return `${x},${y}`;
                  })
                  .join(' ')}
              />

              {/* Actual Spend line (blue-600) - out-of-pocket cost */}
              <polyline
                fill="none"
                stroke={BLUE_DARK}
                strokeWidth="2"
                points={data
                  .map((d, i) => {
                    const x = 40 + (i / (data.length - 1)) * 750;
                    // Use actual for out-of-pocket spend (what we actually paid)
                    const actualSpend = d.actual || 0;
                    const y = 256 - (actualSpend / maxValue) * 230 - 10;
                    return `${x},${y}`;
                  })
                  .join(' ')}
              />

              {/* Account lines - only when account breakdown is available */}
              {hasAccountBreakdown && (
                <>
                  {/* Account AWS Charges line (orange-400) */}
                  <polyline
                    fill="none"
                    stroke="#FB923C"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    points={data
                      .map((d, i) => {
                        if (!d.selected_account) return null;
                        const x = 40 + (i / (data.length - 1)) * 750;
                        const y = 256 - ((d.selected_account.aws_charges || 0) / maxValue) * 230 - 10;
                        return `${x},${y}`;
                      })
                      .filter(p => p !== null)
                      .join(' ')}
                  />

                  {/* Account Credits Used line (yellow-500) */}
                  <polyline
                    fill="none"
                    stroke="#EAB308"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    points={data
                      .map((d, i) => {
                        if (!d.selected_account) return null;
                        const x = 40 + (i / (data.length - 1)) * 750;
                        const y = 256 - ((d.selected_account.credits_consumed || 0) / maxValue) * 230 - 10;
                        return `${x},${y}`;
                      })
                      .filter(p => p !== null)
                      .join(' ')}
                  />

                  {/* Account Actual Spend line (purple-600) */}
                  <polyline
                    fill="none"
                    stroke="#9333EA"
                    strokeWidth="2"
                    strokeDasharray="5,5"
                    points={data
                      .map((d, i) => {
                        if (!d.selected_account) return null;
                        const x = 40 + (i / (data.length - 1)) * 750;
                        const y = 256 - ((d.selected_account.net_spend || 0) / maxValue) * 230 - 10;
                        return `${x},${y}`;
                      })
                      .filter(p => p !== null)
                      .join(' ')}
                  />
                </>
              )}
            </>
          ) : (
            /* Actual line (blue-600) - only when no credits data */
            <polyline
              fill="none"
              stroke={BLUE_DARK}
              strokeWidth="2"
              points={data
                .map((d, i) => {
                  const x = 40 + (i / (data.length - 1)) * 750;
                  const y = 256 - ((d.actual || 0) / maxValue) * 230 - 10;
                  return `${x},${y}`;
                })
                .join(' ')}
            />
          )}

          {/* Hover dots - invisible until hover */}
          {hasCreditsData && data.map((d, i) => {
            const x = 40 + (i / (data.length - 1)) * 750;
            const date = new Date(d.date);
            const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

            return (
              <g key={`hover-${d.date}`}>
                {/* Organization AWS Charges dot */}
                <circle
                  cx={x}
                  cy={256 - ((d.gross_cost || 0) / maxValue) * 230 - 10}
                  r="6"
                  fill={BLUE_LIGHT}
                  className="opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                  stroke="white"
                  strokeWidth="2"
                >
                  <title>{`${dateLabel}\nOrg AWS Charges: $${(d.gross_cost || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}</title>
                </circle>

                {/* Available Credits dot */}
                {d.credits_remaining != null && (
                  <circle
                    cx={x}
                    cy={256 - ((d.credits_remaining || 0) / maxValue) * 230 - 10}
                    r="6"
                    fill={GREEN}
                    className="opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <title>{`${dateLabel}\nAvailable Credits: $${(d.credits_remaining || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}</title>
                  </circle>
                )}

                {/* Org Actual Spend dot */}
                <circle
                  cx={x}
                  cy={256 - ((d.actual || 0) / maxValue) * 230 - 10}
                  r="6"
                  fill={BLUE_DARK}
                  className="opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                  stroke="white"
                  strokeWidth="2"
                >
                  <title>{`${dateLabel}\nOrg Actual Spend: $${(d.actual || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}</title>
                </circle>

                {/* Account dots - only when account breakdown available */}
                {d.selected_account && (
                  <>
                    <circle
                      cx={x}
                      cy={256 - ((d.selected_account.aws_charges || 0) / maxValue) * 230 - 10}
                      r="6"
                      fill="#FB923C"
                      className="opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <title>{`${dateLabel}\nAccount AWS Charges: $${(d.selected_account.aws_charges || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}</title>
                    </circle>

                    <circle
                      cx={x}
                      cy={256 - ((d.selected_account.credits_consumed || 0) / maxValue) * 230 - 10}
                      r="6"
                      fill="#EAB308"
                      className="opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <title>{`${dateLabel}\nAccount Credits Used: $${(d.selected_account.credits_consumed || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}</title>
                    </circle>

                    <circle
                      cx={x}
                      cy={256 - ((d.selected_account.net_spend || 0) / maxValue) * 230 - 10}
                      r="6"
                      fill="#9333EA"
                      className="opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
                      stroke="white"
                      strokeWidth="2"
                    >
                      <title>{`${dateLabel}\nAccount Actual Spend: $${(d.selected_account.net_spend || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`}</title>
                    </circle>
                  </>
                )}
              </g>
            );
          })}

          {/* X-axis labels */}
          {data.map((d, i) => {
            if (!showLabel(i)) return null;
            const x = 40 + (i / (data.length - 1)) * 750;
            const label = formatDateLabel(d.date);
            return (
              <text
                key={d.date}
                x={x}
                y="270"
                textAnchor="middle"
                className="text-xs fill-gray-500 dark:fill-gray-400"
              >
                {label}
              </text>
            );
          })}

          {/* Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((percent) => {
            const value = maxValue * percent;
            const valueInK = value / 1000;
            // Show 1 decimal if needed to avoid duplicate labels
            const label = valueInK >= 10 ? valueInK.toFixed(0) : valueInK.toFixed(1);
            return (
              <text
                key={percent}
                x="35"
                y={256 - percent * 230 + 10 + 4}
                textAnchor="end"
                className="text-xs fill-gray-500 dark:fill-gray-400"
              >
                ${label}k
              </text>
            );
          })}
        </svg>
      </div>

      {/* Current status */}
      {data.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {hasCreditsData ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-3">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">AWS Charges:</span>
                  <span className="ml-2 font-semibold text-blue-400">
                    ${(data[data.length - 1].gross_cost || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Available Credits:</span>
                  <span className="ml-2 font-semibold text-green-500">
                    ${(data[data.length - 1].credits_remaining || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">Actual Spend:</span>
                  <span className="ml-2 font-semibold text-blue-600">
                    ${(data[data.length - 1].actual || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </span>
                </div>
              </div>
              {/* Account breakdown section */}
              {(() => {
                const lastAccount = data[data.length - 1].selected_account;
                const lastGrossCost = data[data.length - 1].gross_cost || 1;
                if (!hasAccountBreakdown || !lastAccount) return null;
                return (
                  <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-3">
                      Selected Account: {lastAccount.account_name}
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">AWS Charges:</span>
                        <span className="ml-2 font-semibold text-orange-600 dark:text-orange-400">
                          ${lastAccount.aws_charges.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Credits Used:</span>
                        <span className="ml-2 font-semibold text-yellow-600 dark:text-yellow-400">
                          ${lastAccount.credits_consumed.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Actual Spend:</span>
                        <span className="ml-2 font-semibold text-purple-600 dark:text-purple-400">
                          ${lastAccount.net_spend.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </div>
                    </div>
                    <p className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                      This account consumed{' '}
                      {((lastAccount.aws_charges / lastGrossCost) * 100).toFixed(1)}%
                      {' '}of organization charges.
                    </p>
                  </div>
                );
              })()}

              <div className="text-xs text-gray-500 dark:text-gray-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded mt-3">
                <strong>Organization Charges:</strong> Total billed by AWS before credits.{' '}
                <strong className="ml-2">Available Credits:</strong> Partner Central/APN + Manual credits remaining.{' '}
                <strong className="ml-2">Actual Spend:</strong> True out-of-pocket cost after all credits.
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Current Planned:</span>
                <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                  ${(data[data.length - 1].planned || 0).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Current Actual:</span>
                <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                  ${(data[data.length - 1].actual || 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BurnRateChart;
